import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';

import { OrgUnitSelectionStatus } from 'src/constants/orgUnit';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import {
  getInitialOrgUnits,
  orgUnitToFilter,
} from 'src/components/common/OrgTree/helper';
import useOrgTree from 'src/components/common/OrgTree/OrgTreeContext';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';
import {
  CHECKBOX_MODE,
  ORG_UNIT_FILTER_NAME,
} from 'src/components/common/OrgTree/types';
import useInitialFilterGroups from 'src/components/followUp/hooks/useInitialFilterGroups';

/**
 * This hook is for controlling the select state of the org tree. It mainly serves two purposes:
 * - Updating the org tree with an initial selected org unit
 * - Providing the select-function that will be called on interaction with the org-tree
 */
const useSelectOrgUnit = (
  selected: string,
  setSelected: Dispatch<SetStateAction<string>>,
  orgTreeState: OrgTreeState,
  checkboxMode: CHECKBOX_MODE,
  syncWithFilter = true,
) => {
  const { initialFilterGroups } = useInitialFilterGroups();
  const { filterGroups, registerOnResetCallback, setFilter } =
    useFilterContext();
  const { orgUnitMap, disableOrgUnitSelectionBasedOnStatus } = useOrgTree();

  const setInitialOrgUnits = useCallback(() => {
    const initialOrgUnits = getInitialOrgUnits(initialFilterGroups, orgUnitMap);

    if (initialOrgUnits.length && syncWithFilter) {
      setSelected(String(initialOrgUnits[0].id));
      orgTreeState.setSelectedOrgUnits(initialOrgUnits);
    }
  }, [
    initialFilterGroups,
    orgUnitMap,
    orgTreeState,
    setSelected,
    syncWithFilter,
  ]);

  // Set initial org unit from filters if a filter with an org unit exists
  useEffect(() => {
    if (String(selected) || !filterGroups.orgUnit) return;

    setInitialOrgUnits();
  }, [filterGroups.orgUnit, selected, setInitialOrgUnits]);

  useEffect(() => {
    const unregister = registerOnResetCallback(setInitialOrgUnits);

    return () => {
      unregister();
    };
  }, [registerOnResetCallback, setInitialOrgUnits]);

  return useCallback(
    (orgUnitId: string) => {
      if (!(orgUnitId in orgUnitMap)) return;

      const { orgUnit } = orgUnitMap[orgUnitId] || {};
      // Early return if org unit status should prevent it from being selected
      if (
        disableOrgUnitSelectionBasedOnStatus &&
        'selection_status' in orgUnit &&
        (orgUnit.selection_status ===
          OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT ||
          orgUnit.selection_status ===
            OrgUnitSelectionStatus.NEGATIVE_SELECTION)
      )
        return;
      setSelected(orgUnitId);
      // Due to a bug in the getReport module where the orgTreeState update causes an infinity loop if done
      // immediatly the orgTreeState update is pushed to the end of the task-queue with setTimeout
      setTimeout(() => {
        orgTreeState.setLatestSelectedOrgUnit(orgUnit);
        orgTreeState.setSelectedOrgUnits([orgUnit]);
      });
      if (checkboxMode === CHECKBOX_MODE.SINGLE && syncWithFilter) {
        setFilter(ORG_UNIT_FILTER_NAME, [orgUnitToFilter(orgUnit)]);
      }
    },
    [
      orgUnitMap,
      orgTreeState,
      checkboxMode,
      setFilter,
      syncWithFilter,
      setSelected,
    ],
  );
};

export default useSelectOrgUnit;
